/**
 * Metric pieces of the design system, shared by web and mobile (Alex,
 * 2026-10-24: "Ensure all pages match the theme, like the metric pages as
 * well"). One spec per piece; each app draws it with its own `MiniBarChart`,
 * `DataTable` and `MetricHelp` (web apps/web/src/components/ui/ds, app
 * apps/mobile/src/components/common/ds), same names and props, except each
 * platform's own spelling of test ids (`testId` / `testID`). MetricHelp is
 * the one piece whose props differ by nature: the web tooltip wraps its
 * trigger (`label`, `icon`, `children`); the app sheet is opened by passing
 * an `explanation` and closed with `onClose`. Both take the same
 * `explanation`.
 *
 *   - MiniBarChart: a small hand-drawn bar trend (a location's weekly cycle
 *     time). Bars `borderStrong`, the latest bar `text.muted`; axis captions
 *     in `meta`. No chart library.
 *   - DataTable: a compact comparison table (departments, top services).
 *     Header cells in the `label` role on the header `band` with a 2px
 *     `borderStrong` rule (like a section band), body cells `body` in
 *     `text.primary` with `borderSubtle` rules, on the page ground `bg`.
 *     Cell padding from `compactSpec.table`.
 *   - MetricHelp: what a figure means, the numbers behind it and how it is
 *     worked out. Web: a tooltip; app: a bottom sheet. Same content shape.
 *
 * This file has NO imports so React Native can deep-import it
 * (`@turn-connect/ui/src/theme/metrics`).
 */

/** The mini bar chart, in px/points. */
export const miniBarSpec = {
  /** Height of the bar area. */
  height: 56,
  /** Space between bars. */
  gap: 6,
  radius: 3,
  /** A bar never shrinks below this share of the height, so an empty week still shows. */
  minPercent: 6,
  /** Space between the bars and the axis captions. */
  captionGap: 4,
} as const;

/** One bar: its value (null draws the minimum stub) and what it says out loud. */
export interface MiniBar {
  key: string;
  value: number | null;
  /** "Week of 2026-10-05: 2.1 d over 14 closed" — the bar's tooltip / spoken text. */
  label: string;
}

/** Each bar's height as a percent of the tallest (at least `miniBarSpec.minPercent`). */
export function miniBarPercents(values: readonly (number | null)[]): number[] {
  const max = Math.max(1, ...values.map((v) => v ?? 0));
  return values.map((v) => Math.max(miniBarSpec.minPercent, ((v ?? 0) / max) * 100));
}

/** The compact data table, in px/points. */
export const dataTableSpec = {
  radius: 8,
  /** Rows on a phone are at least this tall, so a link in a cell is a 44pt target. */
  touchRowHeight: 44,
  /** Header rule under the label band. */
  ruleWidth: 2,
  /** Space between columns (mobile; web uses cell padding). */
  columnGap: 8,
} as const;

/** One explained figure: the shared wording web and app both show. */
export interface MetricHelpEntry {
  key: string;
  title: string;
  /** What it means, in a sentence. */
  meaning: string;
  /** The numbers behind this figure ("312 h turned by 9 techs"). */
  figures: readonly string[];
  /** How it is worked out. */
  calculation: string;
}

/** What a MetricHelp explains: one figure, a row's figures, or a row's flags. */
export interface MetricExplanation {
  /** The heading. Without it a single entry's title heads the card. */
  title?: string;
  /** Lines before the figures (a flag's meaning). */
  intro?: readonly string[];
  helps: readonly MetricHelpEntry[];
  /** Numbers only, one line each ("Hours turned: 312 h · 9 techs"): a row, where the headers hold the definitions. */
  compact?: boolean;
  /** Lines after the figures (the row's flags). */
  notes?: readonly string[];
}

/** The heading a MetricHelp card shows, and whether each entry repeats its own title under it. */
export function metricHelpLayout(e: MetricExplanation): { heading: string | undefined; entryTitles: boolean } {
  const heading = e.title ?? (e.helps.length === 1 ? e.helps[0].title : undefined);
  const entryTitles = e.helps.length > 1 || (!!e.compact && heading !== e.helps[0]?.title);
  return { heading, entryTitles };
}

/** The tooltip (web) and sheet (app), in px/points. */
export const metricHelpSpec = {
  /** Tooltip width on a desktop (never wider than the viewport less the margin). */
  tooltipWidth: 320,
  /** Gap from the trigger, and the margin kept from the viewport's edge. */
  tooltipGap: 8,
  radius: 8,
  /** Space between an entry's lines. */
  gap: 6,
  /** Space between entries. */
  entryGap: 12,
  /** The app's sheet: rounded top, at most this share of the screen. */
  sheetRadius: 16,
  sheetMaxHeight: '75%',
} as const;

/**
 * Stat tile spans for a fixed column count (a narrow panel, where every label
 * fits): each tile spans 1 and the last stretches to fill its row, so no row
 * is left part-empty. (`statTileSpans` in ./textRoles is the phone/wide rule.)
 */
export function fixedTileSpans(count: number, columns: number): number[] {
  return Array.from({ length: count }, (_, i) => (i === count - 1 ? columns - (i % columns) : 1));
}
